import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { Button } from "reactstrap";
import CommonPagination from "../../../../components/Common/TableView/CommonPagination";
import TableHeader from "../../../../components/Common/TableView/TableHeader";
import {
  VENDOR_LOG_CATEGORY_OPTIONS,
  vendorLogActionLabel,
} from "../../../../constant/commonConstants";
import { commonConfirmBox } from "../../../../helper/messagehelpers";
import {
  ActivityLogSvg,
  VendorTrashSvg,
} from "../../../../components/CommonSvg";
import { getInitials } from "../../../../utils/generalUtils";
import {
  clearVendorLogList,
  deleteVendorLogRequest,
  getVendorLogListRequest,
} from "../../../../redux/admin/vendorLog/action";

const EMPTY_FILTER = {
  searchText: "",
  category: "",
  fromDate: null,
  toDate: null,
};

function VendorLogs({
  vendorId: vendorIdProp,
  getVendorLogList,
  deleteVendorLog,
  clearVendorLogs,
  vendorLogList,
  totalRecords,
  isLoading,
}) {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const vendorId = vendorIdProp || id;
  const vendorName = state?.name || "";

  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER);

  const listData = useMemo(
    () => ({
      vendorId: vendorId,
      searchText: appliedFilter.searchText,
      category: appliedFilter.category,
      fromDate: appliedFilter.fromDate
        ? moment(appliedFilter.fromDate).format("YYYY-MM-DD")
        : "",
      toDate: appliedFilter.toDate
        ? moment(appliedFilter.toDate).format("YYYY-MM-DD")
        : "",
      pagination: {
        limit: dataPerPage,
        page: currentPage,
      },
    }),
    [vendorId, appliedFilter, dataPerPage, currentPage]
  );

  useEffect(() => {
    if (!vendorId) {
      return;
    }
    getVendorLogList(listData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData]);

  useEffect(() => {
    return () => clearVendorLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRange = !!(appliedFilter.fromDate && appliedFilter.toDate);
  const hasSearch = !!appliedFilter.searchText;
  const canDelete = hasRange || hasSearch;

  const deleteScopeText = () => {
    const range = hasRange
      ? `between ${moment(appliedFilter.fromDate).format("MM-DD-YYYY")} and ${moment(
          appliedFilter.toDate
        ).format("MM-DD-YYYY")}`
      : "";
    const search = hasSearch ? `matching "${appliedFilter.searchText}"` : "";
    const category = appliedFilter.category
      ? `under ${
          VENDOR_LOG_CATEGORY_OPTIONS.find(
            (option) => option.value === appliedFilter.category
          )?.label || appliedFilter.category
        }`
      : "";
    return [category, range, search].filter(Boolean).join(" and ");
  };

  const handleDeleteFiltered = async () => {
    if (!canDelete) {
      return;
    }
    const confirmed = await commonConfirmBox(
      "Clear the activity shown here?",
      "Yes, clear it",
      "Cancel",
      `Every activity record of this vendor ${deleteScopeText()} will be permanently deleted. This cannot be undone.`
    );
    if (confirmed) {
      setCurrentPage(1);
      deleteVendorLog({
        vendorId: vendorId,
        category: appliedFilter.category,
        fromDate: hasRange
          ? moment(appliedFilter.fromDate).format("YYYY-MM-DD")
          : "",
        toDate: hasRange
          ? moment(appliedFilter.toDate).format("YYYY-MM-DD")
          : "",
        searchText: hasSearch ? appliedFilter.searchText : "",
        listData: listData,
      });
    }
  };

  const isFiltered =
    !!appliedFilter.searchText ||
    !!appliedFilter.category ||
    !!appliedFilter.fromDate ||
    !!appliedFilter.toDate;

  const isBlank = !isLoading && !isFiltered && totalRecords === 0;

  return (
    <div className="container">
      <TableHeader
        hedingName={`Activity Log${vendorName ? ` — ${vendorName}` : ""}`}
        back={() =>
          /* hand the list back exactly what it gave us, so Back returns to the
             page and filter the user left rather than to a fresh first page */
          navigate("/admin/vendor-list", {
            state: { listState: state?.listState },
          })
        }
        searchBar={true}
        dataPerPage={dataPerPage}
        currentPage={currentPage}
        updateData={() => {}}
        setSearchData={(text) => {
          setCurrentPage(1);
          setAppliedFilter((prev) => ({ ...prev, searchText: text || "" }));
        }}
        Filter={!isBlank}
        filterType={"vendorLog"}
        logFilter={appliedFilter}
        setLogFilter={(next) => {
          setCurrentPage(1);
          setAppliedFilter((prev) => ({ ...prev, ...next }));
        }}
        logFilterActions={
          canDelete &&
          totalRecords > 0 && (
            <button
              className="vendor_log_delete_btn"
              onClick={handleDeleteFiltered}
            >
              <VendorTrashSvg size={14} />
              Delete Activity
            </button>
          )
        }
      />

      <div className="vendor_log_wrap">

      {isLoading ? (
        <div className="vendor_log_skeleton">
          {[0, 1, 2, 3, 4].map((row) => (
            <div className="vendor_log_skeleton_row" key={row}>
              <span className="vendor_log_skeleton_avatar" />
              <span className="vendor_log_skeleton_line" />
              <span className="vendor_log_skeleton_line short" />
            </div>
          ))}
        </div>
      ) : vendorLogList.length > 0 ? (
        <div className="table-responsive">
          <table className="table vendor_log_table">
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Activity</th>
                <th>Date &amp; Time</th>
              </tr>
            </thead>
            <tbody>
              {vendorLogList.map((log) => (
                <tr key={log.id}>
                  <td>
                    <div className="vendor_log_user_cell">
                      <span className="vendor_log_avatar">
                        {getInitials(log.VendorUsers?.contactName) || "?"}
                      </span>
                      <div>
                        <span className="vendor_log_user">
                          {log.VendorUsers?.contactName || "Unknown user"}
                        </span>
                        <span className="vendor_log_email">
                          {log.VendorUsers?.userEmail || ""}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`vendor_log_action log-${log.actionType}`}>
                      {vendorLogActionLabel(log.actionType)}
                    </span>
                  </td>
                  <td className="vendor_log_reference">
                    {log.message || "-"}
                  </td>
                  <td className="vendor_log_time">
                    {log.createdDate
                      ? moment(log.createdDate).format("MM-DD-YYYY hh:mm A")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="vendor_log_empty">
          <span className="vendor_log_empty_icon">
            <ActivityLogSvg />
          </span>
          <h6>{isFiltered ? "No matching activity" : "No activity yet"}</h6>
          <p>
            {isFiltered
              ? "No log entry matches the search or date range you applied."
              : "Nothing has been recorded for this vendor's users so far."}
          </p>
          {isFiltered && (
            <Button className="vendor_log_empty_reset" onClick={() => {
                setCurrentPage(1);
                setAppliedFilter(EMPTY_FILTER);
              }}>
              Reset filters
            </Button>
          )}
        </div>
      )}

      {totalRecords > 0 && (
        <CommonPagination
          dataPerPage={dataPerPage}
          totalData={totalRecords}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setDataPerPage={setDataPerPage}
          direction="up"
        />
      )}
      </div>
    </div>
  );
}

const mapStateToProps = ({ adminVendorLog }) => {
  const vendorLogList =
    adminVendorLog?.getVendorLogs?.list?.length > 0
      ? adminVendorLog.getVendorLogs.list
      : [];
  const totalRecords = adminVendorLog?.getVendorLogs?.totalRecords || 0;
  const isLoading = !adminVendorLog?.getVendorLogs;
  return { vendorLogList, totalRecords, isLoading };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getVendorLogList: (data) => dispatch(getVendorLogListRequest(data)),
    deleteVendorLog: (data) => dispatch(deleteVendorLogRequest(data)),
    clearVendorLogs: () => dispatch(clearVendorLogList()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorLogs);
